import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { chatbotAPI, paymentAPI, handleAPIError } from '../services/api';
import { getPlansData } from '../config/stripe';
import { 
  X,
  Check,
  Star,
  Zap,
  Users,
  MessageCircle,
  Globe,
  Shield,
  CreditCard,
  Gift,
  Crown
} from 'lucide-react';

const PlanSelectionModal = ({ chatbot, isOpen, onClose, onUpdate }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1: final payment, 2: plan selection
  const [finalPaymentCompleted, setFinalPaymentCompleted] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Load plans data when modal opens
  React.useEffect(() => {
    const loadPlansData = async () => {
      if (isOpen) {
        try {
          setPlansLoading(true);
          const plansData = await getPlansData();
          setPlans(plansData);
        } catch (error) {
          console.error('Error loading plans data:', error);
          toast({
            title: "Configuration Error",
            description: "Failed to load pricing plans. Please try again.",
            variant: "destructive",
          });
        } finally {
          setPlansLoading(false);
        }
      }
    };

    loadPlansData();
  }, [isOpen, toast]);

  const handleFinalPayment = async () => {
    setLoading(true);
    try {
      const response = await paymentAPI.createFinalPaymentCheckoutSession(chatbot.id);
      
      // Handle the case where final payment is already completed
      if (response.data && response.data.redirect_to_plan_selection) {
        toast({
          title: "Final Payment Already Completed ✅",
          description: response.data.message || "You can now select your subscription plan.",
        });
        setCurrentStep(2);
        setFinalPaymentCompleted(true);
        setLoading(false);
        return;
      }
      
      // Normal case: redirect to Stripe checkout
      if (response.data && response.data.checkout_url) {
        // Store the current state to resume after payment
        localStorage.setItem('pending_final_payment_chatbot_id', chatbot.id);
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast({
        title: "Payment Failed",
        description: errorMessage,
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const handlePlanSelection = async () => {
    if (!selectedPlan) {
      toast({
        title: "No Plan Selected",
        description: "Please select a subscription plan to continue",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await paymentAPI.createPlanSelectionCheckoutSession(chatbot.id, selectedPlan.id);
      if (response.data && response.data.checkout_url) {
        // Store the plan selection for post-payment processing
        localStorage.setItem('selected_plan_id', selectedPlan.id);
        localStorage.setItem('selected_plan_type', selectedPlan.type);
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast({
        title: "Plan Selection Failed",
        description: errorMessage,
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  // Check if final payment is completed when modal opens
  React.useEffect(() => {
    const checkFinalPaymentStatus = async () => {
      if (isOpen && chatbot) {
        try {
          // Check final payment status from API
          const response = await paymentAPI.getFinalPaymentStatus();
          const finalPaymentCompleted = response.data.final_payment_completed;
          
          if (finalPaymentCompleted) {
            setCurrentStep(2);
            setFinalPaymentCompleted(true);
            toast({
              title: "Final Payment Completed ✅",
              description: "Your final payment was successful. Please select your subscription plan.",
            });
          } else {
            setCurrentStep(1);
            setFinalPaymentCompleted(false);
          }
        } catch (error) {
          console.error('Error checking final payment status:', error);
          // Fallback to chatbot status if API fails
          if (chatbot.final_payment_paid) {
            setCurrentStep(2);
            setFinalPaymentCompleted(true);
          } else {
            setCurrentStep(1);
            setFinalPaymentCompleted(false);
          }
        }
      }
    };

    checkFinalPaymentStatus();
  }, [isOpen, chatbot]);

  if (!isOpen || !chatbot) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-gray-900 border-violet-500/20">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-white flex items-center">
                <CreditCard className="w-8 h-8 mr-3 text-violet-400" />
                Final Payment & Plan Selection
              </CardTitle>
              <CardDescription className="text-gray-400 mt-2">
                Complete your chatbot setup with final payment and choose your subscription plan
              </CardDescription>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 ${currentStep >= 1 ? 'text-violet-400' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep >= 1 && finalPaymentCompleted ? 'bg-green-600' : 
                  currentStep >= 1 ? 'bg-violet-600' : 'bg-gray-600'
                } text-white`}>
                  {finalPaymentCompleted ? <Check className="w-4 h-4" /> : '1'}
                </div>
                <span className="text-sm font-medium">Final Payment</span>
              </div>
              <div className={`w-8 h-0.5 ${currentStep >= 2 ? 'bg-violet-400' : 'bg-gray-400'}`}></div>
              <div className={`flex items-center space-x-2 ${currentStep >= 2 ? 'text-violet-400' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep >= 2 ? 'bg-violet-600' : 'bg-gray-600'
                } text-white`}>
                  2
                </div>
                <span className="text-sm font-medium">Plan Selection</span>
              </div>
            </div>
          </div>

          {/* Step 1: Final Payment */}
          {currentStep === 1 && !finalPaymentCompleted && (
            <div className="mb-8">
              <Card className="bg-gray-800/50 border-violet-500/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <div className="w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center mr-3">
                      1
                    </div>
                    Final Payment - $400
                  </CardTitle>
                  <CardDescription>
                    Complete the remaining setup fee for your chatbot
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-violet-900/20 border border-violet-500/30 rounded-lg p-4 mb-4">
                    <p className="text-violet-300 text-sm">
                      <Gift className="w-4 h-4 inline mr-1" />
                      Your chatbot is ready for testing! After completing the final payment, you'll be able to select your subscription plan.
                    </p>
                  </div>
                  <div className="text-white">
                    <p className="mb-2">Setup fee breakdown:</p>
                    <div className="space-y-1 text-sm text-gray-300">
                      <div className="flex justify-between">
                        <span>Initial deposit (paid):</span>
                        <span className="text-green-400">$200</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span>Remaining amount:</span>
                        <span className="text-violet-400">$400</span>
                      </div>
                      <div className="border-t border-gray-600 pt-1 mt-2 flex justify-between font-semibold">
                        <span>Total setup fee:</span>
                        <span>$600</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <Button
                      onClick={handleFinalPayment}
                      disabled={loading}
                      className="bg-violet-600 hover:bg-violet-700 w-full"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Pay Final $400
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 2: Plan Selection */}
          {(currentStep === 2 || finalPaymentCompleted) && (
            <div className="mb-8">
              <Card className="bg-gray-800/50 border-violet-500/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3">
                    2
                  </div>
                  Choose Your Subscription Plan
                </CardTitle>
                <CardDescription>
                  Select a monthly plan to keep your chatbot running
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
                  <p className="text-blue-300 text-sm flex items-center">
                    <Gift className="w-4 h-4 mr-2" />
                    🎉 <strong>Special Offer:</strong> Get your first month FREE with any plan! Start your trial today.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {plansLoading ? (
                    // Loading skeleton for plans
                    <>
                      {[1, 2, 3].map((i) => (
                        <Card key={i} className="bg-gray-800/50 border-gray-600 animate-pulse">
                          <CardHeader>
                            <div className="w-12 h-12 rounded-lg bg-gray-700 mb-4"></div>
                            <div className="h-6 bg-gray-700 rounded mb-2"></div>
                            <div className="h-8 bg-gray-700 rounded mb-2"></div>
                            <div className="h-4 bg-gray-700 rounded"></div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {[1, 2, 3, 4].map((j) => (
                                <div key={j} className="h-4 bg-gray-700 rounded"></div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </>
                  ) : (
                    plans.map((plan) => (
                    <Card 
                      key={plan.id}
                      className={`relative cursor-pointer transition-all duration-300 ${
                        selectedPlan?.id === plan.id 
                          ? 'ring-2 ring-violet-500 bg-violet-900/20 border-violet-500/50' 
                          : 'bg-gray-800/50 border-gray-600 hover:border-violet-500/30'
                      } ${plan.popular ? 'ring-1 ring-purple-500/50' : ''}`}
                      onClick={() => setSelectedPlan(plan)}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <Badge className="bg-purple-600 text-white px-3 py-1">
                            <Star className="w-3 h-3 mr-1" />
                            Most Popular
                          </Badge>
                        </div>
                      )}
                      
                      <CardHeader className="pb-4">
                        <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                          <plan.icon className="w-6 h-6 text-white" />
                        </div>
                        <CardTitle className="text-white text-lg">{plan.name}</CardTitle>
                        <div className="text-2xl font-bold text-white">
                          ${plan.price}
                          <span className="text-sm font-normal text-gray-400">/{plan.period}</span>
                        </div>
                        {plan.monthlyEquivalent && (
                          <div className="text-sm text-gray-400">
                            ${plan.monthlyEquivalent.toFixed(2)}/month
                          </div>
                        )}
                        <div className="text-center">
                          <Badge className="bg-green-600/20 text-green-300 border-green-500/30">
                            <Gift className="w-3 h-3 mr-1" />
                            First month FREE
                          </Badge>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="pt-0">
                        <ul className="space-y-2">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-center text-sm text-gray-300">
                              <Check className="w-4 h-4 mr-2 text-green-400 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        
                        {selectedPlan?.id === plan.id && (
                          <div className="mt-4 p-2 bg-violet-600/20 rounded-lg">
                            <p className="text-violet-300 text-xs text-center">
                              ✓ Selected Plan
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            
            {/* Final Payment Step Action */}
            {currentStep === 1 && !finalPaymentCompleted && (
              <Button
                onClick={handleFinalPayment}
                disabled={loading}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay Final $400
                  </>
                )}
              </Button>
            )}
            
            {/* Plan Selection Step Action */}
            {(currentStep === 2 || finalPaymentCompleted) && (
              <Button
                onClick={handlePlanSelection}
                disabled={loading || !selectedPlan}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Subscribe to {selectedPlan ? selectedPlan.name : 'Selected Plan'}
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlanSelectionModal;